import * as THREE from 'three';
import type { City } from '../world/city';
import type { Vec } from '../destruction/structure';
import type { PhysicsCity } from '../physics/world';
import type { Person, Population, TrafficVehicle } from './population';
import type { Soldier } from './military';
import type { Aircraft, FlightLeg } from './aviation';
import type { NavalShip } from './naval';
import type { NavalLanding } from '../world/strategic-transport';
import type { WalkNode } from './walking';
import { Combat, type CombatTarget, type Weapon } from './combat';
import { CombatCharges } from './combat-charges';
import { CityCapacity } from './city-capacity';
import { combatCapable as alive, combatTurn, rosterCounts, invasionOutcome } from './invasion-roster';
import { isMilitaryVehicle } from '../render/military-vehicles';
import { MILITARY_BALANCE as BALANCE } from './military-balance';

interface LandingGroup {
  ship: NavalShip;
  landing: NavalLanding;
  crew: Soldier[];
  unloading: boolean;
  returning: boolean;
}
interface AirGroup {
  aircraft: Aircraft;
  crew: Soldier[];
  index: number;
  dropping: boolean;
  returning: boolean;
}
const v = (x: number, y: number, z: number) => new THREE.Vector3(x, y, z);
// Defend inhabited neighbourhoods instead of massing the whole friendly roster
// across the same shoreline that every arriving hostile has to pass.
const DEFENCE_POSTS = [
  v(-360, 0, 180),
  v(-180, 0, -180),
  v(0, 0, 60),
  v(180, 0, 250),
  v(380, 0, -80),
  v(600, 0, 200),
];
const DROP_CORRIDORS = [520, 380, -390, -600];
/** A finite, original-identity event. Preparation yields between resources and
 * actor batches; perception is spatial and round-robin. Ordinary damage, fire,
 * collision, hospital care, navigation and reconstruction remain authoritative. */
export class Invasion {
  phase: 'idle' | 'preparing' | 'deploying' | 'combat' | 'ended' = 'idle';
  serial = 0;
  time = 0;
  reason = '';
  result = '';
  hostile: Soldier[] = [];
  readonly hostileById = new Map<string, Soldier>();
  readonly combat: Combat;
  readonly charges: CombatCharges;
  readonly capacity: CityCapacity;
  reserved = 0;
  unavailable = 0;
  cancelledReserves = 0;
  onMessage: (text: string) => void = () => {};
  log: { time: number; action: string }[] = [];
  private epoch = 0;
  private deployment?: Generator<void>;
  private boats: LandingGroup[] = [];
  private flights: AirGroup[] = [];
  private retired: Soldier[] = [];
  private retiredById = new Map<string, Soldier>();
  private cursor = 0;
  private cleanupCursor = 0;
  private counterAge = 0;
  private fleetAge = 0;
  private bombingAt = 18;
  private vehicleCursor = 0;
  private decisions = new Map<string, number>();
  private targetCursor = new Map<string, number>();
  private nextFire = new Map<string, number>();
  private nextCharge = new Map<string, number>();
  private neutralTask = new Map<string, string>();
  private taskSerial = 0;
  private originVehicles = new Map<string, THREE.Vector3>();
  private goalNodes = new Map<string, WalkNode[]>();
  private march = new Map<string, { token: string; point: THREE.Vector3; time: number }>();
  private landingEgress = new Map<
    string,
    { soldier: Soldier; landing: NavalLanding; token?: string; retry: number }
  >();
  private civilianBuildings: Set<number>;
  setupMaxMs = 0;
  constructor(
    readonly city: City,
    readonly physics: PhysicsCity,
    readonly population: Population,
  ) {
    this.combat = new Combat(physics, population);
    this.charges = new CombatCharges(physics, population);
    this.capacity = new CityCapacity(city, physics);
    this.epoch = physics.life.epoch;
    const military = new Set(city.strategic?.parcels.find((p) => p.id === 'military')?.buildings ?? []);
    this.civilianBuildings = new Set(
      city.buildings.filter((b) => !military.has(b.id) && b.category !== 'prop').map((b) => b.id),
    );
    if (population.naval)
      population.naval.passengers.onLand = (p, ship) => {
        const s = this.hostileById.get(p.id) ?? this.retiredById.get(p.id);
        if (s) {
          s.state = this.active && !p.demobilised ? 'deployed' : 'withdrawn';
          p.layer = 'street';
          p.controlled = true;
          const landing = this.boats.find((g) => g.ship === ship)?.landing;
          if (landing) this.landingEgress.set(p.id, { soldier: s, landing, retry: 0 });
        }
      };
  }
  get active(): boolean {
    return this.phase === 'preparing' || this.phase === 'deploying' || this.phase === 'combat';
  }
  private record(action: string): void {
    this.log.push({ time: this.time, action });
    if (this.log.length > 256) this.log.shift();
  }
  start(): boolean {
    if (
      this.active ||
      this.physics.life.phase !== 'playing' ||
      !this.population.military?.friendly.length ||
      !this.population.naval ||
      !this.population.aviation
    )
      return false;
    // Old survivors remain original, disarmed actors or departing passengers;
    // subsequent explicit starts never recreate friendly casualties.
    this.retired.push(...this.hostile);
    for (const s of this.hostile) this.retiredById.set(s.id, s);
    this.hostile = [];
    this.hostileById.clear();
    this.reserved = 600;
    this.unavailable = this.cancelledReserves = 0;
    this.serial++;
    this.epoch = this.physics.life.epoch;
    this.phase = 'preparing';
    this.bombingAt = this.time + 12;
    this.result = '';
    this.reason = 'Preparing finite transports and 600 original hostile assignments';
    this.goalNodes.clear();
    this.march.clear();
    this.decisions.clear();
    this.targetCursor.clear();
    this.nextFire.clear();
    this.nextCharge.clear();
    this.capacity.beginCapture();
    this.combat.start();
    this.charges.reset();
    this.population.military.mobilise();
    this.population.shelters?.start();
    if (this.population.drivers) this.population.drivers.alert = true;
    this.deployment = this.deploy(this.serial, this.epoch);
    this.counterAge = this.fleetAge = 0;
    for (const car of this.population.cars.filter((c) => c.id.startsWith('military:')))
      this.originVehicles.set(car.id, car.p.clone());
    this.record(`Explicit event ${this.serial}: 600 committed hostiles; existing friendly survivors reused`);
    return true;
  }
  private soldier(index: number, point: Vec, role: string): Soldier {
    const id = `crew:military:hostile:${this.serial}:${index}`,
      p = this.population.createCrew(id, point);
    p.name = `Grey ${role} ${index + 1}`;
    p.faction = 'hostile';
    p.color = 0x8b9295;
    p.speed = 2.6 * BALANCE.deploymentPace;
    p.combatHealth = BALANCE.hostileHealth;
    p.scale = 1;
    const s: Soldier = {
      id,
      person: p,
      faction: 'hostile',
      index,
      state: 'aboard',
      role,
      task: '',
      sequence: 0,
      retry: 0,
      checks: 0,
      route: [],
      cursor: 0,
      cooldown: 0,
      think: 0,
    };
    this.hostile.push(s);
    this.hostileById.set(id, s);
    this.reserved--;
    return s;
  }
  private *deploy(event: number, epoch: number): Generator<void> {
    const naval = this.population.naval!,
      aviation = this.population.aviation!,
      landings = this.city.strategic!.transport!.landings;
    for (let i = 0; i < 2; i++) {
      if (!this.active || event !== this.serial || epoch !== this.physics.life.epoch) return;
      const landing = landings[i + 1];
      let ship: NavalShip | undefined;
      for (let attempt = 0; attempt < 6 && !ship; attempt++) {
        ship = naval.create(`invasion:${event}:${i}`, 'hostile', {
          x: landing.berth.x,
          y: landing.berth.y,
          z: 835 + attempt * 24,
        });
        yield;
      }
      if (!ship) {
        this.reserved -= 100;
        this.unavailable += 100;
        this.record(`Naval group ${i + 1} unavailable: no real clear water origin`);
        continue;
      }
      const group: LandingGroup = { ship, landing, crew: [], unloading: false, returning: false };
      ship.boat.speed *= BALANCE.deploymentPace;
      this.boats.push(group);
      for (let seat = 0; seat < 100; seat++) {
        const s = this.soldier(
          i * 100 + seat,
          naval.seat(ship, seat),
          seat === 3 || seat === 4 ? 'naval operator' : seat % 6 === 0 ? 'rocketeer' : 'infantry',
        );
        if (!naval.passengers.attach(ship, s.person, seat))
          throw new Error(`Invalid original naval position ${s.id}`);
        s.seat = seat;
        group.crew.push(s);
        if (seat % 8 === 7) yield;
      }
      ship.pilots = group.crew.filter((s) => s.role === 'naval operator').map((s) => s.id);
      if (
        !naval.setRoute(
          ship,
          [{ ...landing.berth, z: 805 }, landing.berth],
          'Finite grey transport approaching its supported pier',
        )
      )
        this.record(`Naval approach unavailable: ${ship.reason}`);
      yield;
    }
    for (let i = 0; i < 4; i++) {
      if (!this.active || event !== this.serial || epoch !== this.physics.life.epoch) return;
      const sign = i % 2 ? -1 : 1,
        height = 200 + i * 35,
        z = DROP_CORRIDORS[i];
      const aircraft = aviation.createMission(
        `invasion:${event}:${i}`,
        'hostile',
        { x: -sign * 1190, y: height, z },
        { x: sign, y: 0, z: 0 },
      );
      const group: AirGroup = { aircraft, crew: [], index: i, dropping: false, returning: false };
      this.flights.push(group);
      yield;
      for (let slot = 0; slot < 100; slot++) {
        const s = this.soldier(
          200 + i * 100 + slot,
          aviation.troops.point(aircraft, aviation.troops.slot(slot)),
          slot < 2 ? 'flight operator' : slot % 6 === 0 ? 'rocketeer' : 'infantry',
        );
        if (!aviation.troops.attach(aircraft, s, slot))
          throw new Error(`Invalid original cargo position ${s.id}`);
        group.crew.push(s);
        if (slot % 4 === 3) yield;
      }
      aircraft.pilots = group.crew.slice(0, 2).map((s) => s.id);
      aviation.routeMission(
        aircraft,
        this.circuit(group),
        'Finite grey aircraft delivering its original manifest',
      );
      yield;
    }
    this.phase = 'deploying';
    this.reason = 'Original naval and air manifests approaching the city';
    this.record(
      `${this.hostile.length} hostile identities created; ${this.unavailable} unavailable transport assignments`,
    );
  }
  private circuit(g: AirGroup): FlightLeg[] {
    const sign = g.index % 2 ? -1 : 1,
      h = 200 + g.index * 35,
      z = DROP_CORRIDORS[g.index];
    return [
      { point: v(sign * 1190, h, z), speed: 35 * BALANCE.deploymentPace, state: 'excursion' },
      { point: v(sign * 1280, h + 25, 840), speed: 45 * BALANCE.deploymentPace, state: 'excursion' },
      { point: v(-sign * 1280, h + 25, 840), speed: 45 * BALANCE.deploymentPace, state: 'excursion' },
      { point: v(-sign * 1190, h, z), speed: 40 * BALANCE.deploymentPace, state: 'excursion' },
    ];
  }
  private fleet(): void {
    const naval = this.population.naval!,
      aviation = this.population.aviation!;
    for (const g of this.boats) {
      if (g.ship.state === 'wreck' || !naval.ships.includes(g.ship)) continue;
      const delivered =
          g.unloading &&
          g.crew.every(
            (s) =>
              g.ship.pilots?.includes(s.id) ||
              !alive(s) ||
              naval.passengers.people.get(s.id)?.state === 'landed',
          ),
        current =
          !g.returning && !delivered && g.crew.some((s) => this.hostileById.get(s.id) === s) && this.active;
      if (current) {
        if (!g.unloading && g.ship.state === 'moored')
          g.unloading = naval.passengers.disembark(g.ship, g.landing, new Set(g.ship.pilots));
        if (g.ship.state === 'blocked' && naval.time - g.ship.blockedSince > 12) {
          const alternative = this.city.strategic!.transport!.landings.find(
            (l) =>
              l !== g.landing &&
              !this.boats.some(
                (other) => other !== g && other.landing === l && other.ship.state !== 'wreck',
              ) &&
              !naval.ships.some(
                (s) => s !== g.ship && s.boat.p.distanceTo(new THREE.Vector3().copy(l.berth)) < 30,
              ),
          );
          if (
            alternative &&
            naval.setRoute(
              g.ship,
              [
                { x: g.ship.boat.p.x, y: g.ship.boat.p.y, z: 805 },
                { ...alternative.berth, z: 805 },
                alternative.berth,
              ],
              'Blocked landing diverted through a real alternative berth',
            )
          )
            g.landing = alternative;
        }
      } else if (!g.returning && !naval.passengers.crossingGangway(g.ship)) {
        g.returning = naval.setRoute(
          g.ship,
          [{ x: g.ship.boat.p.x, y: g.ship.boat.p.y, z: 850, reverse: true }],
          delivered
            ? 'Delivery complete; original operators clearing the berth and returning offshore'
            : 'Non-hostile withdrawal to the offshore boundary',
        );
      } else if (g.returning && !g.ship.route.length && g.ship.boat.p.z >= 849) {
        for (const s of g.crew) {
          const p = naval.passengers.people.get(s.id);
          if (p?.state === 'aboard' && !s.person.fatal) {
            p.state = 'landed';
            s.person.carrier = undefined;
            s.state = 'withdrawn';
            this.population.retireCrew(s.person);
          }
        }
        naval.retire(g.ship);
      }
    }
    for (const g of this.flights) {
      // The finite manifest is assembled over several frames. Do not withdraw
      // its aircraft just because the first yielded batch has no people yet.
      if (this.deployment && g.crew.length < 100) continue;
      const a = g.aircraft,
        delivered =
          g.dropping &&
          g.crew.every(
            (s) =>
              s.role === 'flight operator' ||
              !alive(s) ||
              aviation.troops.people.get(s.id)?.state === 'departed',
          ),
        current =
          !g.returning && !delivered && g.crew.some((s) => this.hostileById.get(s.id) === s) && this.active;
      if (a.state === 'wreck' || !aviation.aircraft.includes(a)) continue;
      if (this.population.airstrikes?.busy(a)) {
        // Airstrike cabin clearance cancels the old drop queue. Remaining
        // originals need a fresh queue when their delivery circuit resumes.
        g.dropping = false;
        continue;
      }
      if (current) {
        if (!a.route.length)
          aviation.routeMission(
            a,
            this.circuit(g),
            'Original remaining manifest on another supported drop pass',
          );
        if (!g.dropping && a.p.y > 80) {
          const destinations = new Map(
            g.crew
              .filter((s) => s.role !== 'flight operator' && s.person.carrier === a.id && !s.person.fatal)
              .map((s) => [s.id, { x: s.person.p.x, y: 0, z: s.person.p.z }]),
          );
          g.dropping = aviation.troops.beginDrop(a, destinations);
        }
      } else if (!g.returning) {
        aviation.troops.cancel(a);
        g.returning = aviation.routeMission(
          a,
          [{ point: v(a.p.x < 0 ? -1340 : 1340, Math.max(200, a.p.y), 860), speed: 48, state: 'excursion' }],
          delivered
            ? 'Delivery complete; original flight operators returning offshore'
            : 'Non-hostile aircraft withdrawal with original remaining occupants',
        );
      } else if (!a.route.length && Math.abs(a.p.x) > 1330) {
        for (const s of g.crew) {
          const o = aviation.troops.people.get(s.id);
          if (o && ['aboard', 'returning'].includes(o.state) && !s.person.fatal) {
            o.state = 'departed';
            s.person.carrier = undefined;
            s.state = 'withdrawn';
            this.population.retireCrew(s.person);
          }
        }
        aviation.retireMission(a);
      }
    }
  }
  private nodes(key: string, point: Vec, radius: number): WalkNode[] {
    let nodes = this.goalNodes.get(key);
    if (!nodes) {
      nodes = this.population
        .civil!.network.near(point, radius, 'street')
        .filter((n) => !n.restricted && n.y < 20);
      this.goalNodes.set(key, nodes);
    }
    return nodes;
  }
  private route(s: Soldier, point: Vec, action: string, indoor = false): boolean {
    const civil = this.population.civil!,
      p = s.person,
      task = civil.task(p);
    if (task?.token === s.task && task.state === 'active') {
      const progress = this.march.get(s.id);
      if (!progress || progress.token !== task.token || progress.point.distanceToSquared(p.p) > 1) {
        this.march.set(s.id, { token: task.token, point: p.p.clone(), time: this.time });
        return true;
      }
      // Leave queued planning alone. A physically stalled, already-routed
      // soldier releases its old destination and tries a different reserved slot.
      if (this.time - progress.time < 8 || civil.taskPlanning(p)) return true;
      civil.cancelTask(p, 'Blocked combat approach; trying another supported position');
      this.march.delete(s.id);
      s.sequence += 13;
    }
    if (!civil.prepareForTask(p)) return false;
    const key = `${indoor ? 'building' : 'street'}:${Math.round(point.x / 24)}:${Math.round(point.z / 24)}`;
    let nodes = this.goalNodes.get(key);
    if (!nodes) {
      nodes = indoor
        ? civil.network
            .near(point, 80)
            .filter(
              (n) =>
                n.buildingId !== undefined &&
                this.civilianBuildings.has(n.buildingId) &&
                n.layer.startsWith('interior:') &&
                Number(n.layer.split(':')[2]) <= 1,
            )
        : this.nodes(key, point, 42);
      nodes.sort(
        (a, b) => (a.x - point.x) ** 2 + (a.z - point.z) ** 2 - ((b.x - point.x) ** 2 + (b.z - point.z) ** 2),
      );
      this.goalNodes.set(key, nodes);
    }
    const usable = nodes.slice(0, Math.min(80, nodes.length));
    for (let i = 0; i < Math.min(8, usable.length); i++) {
      const node = usable[(s.index * 7 + s.sequence + i) % usable.length];
      if (this.population.hazardAt(node) || !civil.network.available(civil.nodePlace(node))) continue;
      const token = `invasion:${this.serial}:${s.id}:${++s.sequence}`;
      if (civil.assignTask(p, civil.nodePlace(node), token, action, 2)) {
        s.task = token;
        return true;
      }
    }
    return false;
  }
  private target(s: Soldier): CombatTarget | undefined {
    const candidates: CombatTarget[] = [],
      p = s.person;
    this.population.actorsNear(p.p, 165, (a) => {
      if (a.category === 'animal' || a.hidden || a.id === p.id) return;
      if (a.category === 'person') {
        const person = a as Person;
        if (
          person.fatal ||
          person.demobilised ||
          ['incapacitated', 'falling', 'carried', 'recovering'].includes(person.state)
        )
          return;
        if (person.carrier && !person.exposedSeat) return;
        if (s.faction === 'friendly' ? !this.hostileById.has(person.id) : person.faction === 'hostile')
          return;
      } else {
        const car = a as TrafficVehicle;
        if (car.state === 'wreck') return;
        const hostile = this.boats.some(
          (g) => g.ship.boat === car && g.crew.some((u) => this.hostileById.has(u.id)),
        );
        if (s.faction === 'friendly' ? !hostile : hostile) return;
      }
      candidates.push(a as CombatTarget);
    });
    const priority = (a: CombatTarget) => {
      if (a.category !== 'person') return isMilitaryVehicle(a.kind) ? 0 : 50000;
      if (a.state === 'parachuting') return 40000;
      return a.faction === (s.faction === 'friendly' ? 'hostile' : 'friendly') ? 0 : 60000;
    };
    candidates.sort(
      (a, b) => priority(a) - priority(b) || a.p.distanceToSquared(p.p) - b.p.distanceToSquared(p.p),
    );
    const car = s.vehicle && this.population.military!.isGunner(s) ? s.vehicle : undefined;
    // Always consider the nearest priority opponents, then rotate the remaining
    // bounded probes. Six hidden opponents must not make every visible soldier
    // behind them permanently unselectable.
    for (let i = 0; i < Math.min(2, candidates.length); i++)
      if (this.combat.visible(p, candidates[i], car)) return candidates[i];
    const remaining = candidates.length - 2;
    if (remaining <= 0) return;
    const start = (this.targetCursor.get(s.id) ?? 0) % remaining,
      checks = Math.min(4, remaining);
    this.targetCursor.set(s.id, (start + checks) % remaining);
    for (let i = 0; i < checks; i++) {
      const candidate = candidates[2 + ((start + i) % remaining)];
      if (this.combat.visible(p, candidate, car)) return candidate;
    }
  }
  private brain(s: Soldier): void {
    const p = s.person,
      military = this.population.military!;
    if (
      !alive(s) ||
      p.state === 'falling' ||
      s.transition ||
      s.state === 'airborne' ||
      this.charges.active.has(s.id)
    )
      return;
    if (s.faction === 'friendly' && s.assignment === 'naval' && !military.maritime.availableForDefence(s))
      return;
    const gunner = s.faction === 'friendly' && military.isGunner(s) && s.state === 'aboard',
      car = gunner ? s.vehicle : undefined;
    if (p.carrier && !gunner) return;
    if ((this.decisions.get(s.id) ?? 0) > this.time) return;
    this.decisions.set(s.id, this.time + 0.45);
    const target = this.target(s);
    if (target) {
      const aim = this.combat.targetPoint(target).sub(this.combat.muzzle(p, car));
      if (car) {
        car.turretYaw = Math.atan2(-aim.x, -aim.z) - car.yaw;
        car.turretPitch = Math.atan2(aim.y, Math.hypot(aim.x, aim.z));
      } else p.yaw = Math.atan2(-aim.x, -aim.z);
      if ((this.nextFire.get(s.id) ?? 0) <= this.time) {
        const weapon: Weapon =
          car?.kind === 'tank' ? 'tank' : car ? 'roof-gun' : s.role === 'rocketeer' ? 'rocket' : 'rifle';
        if (this.combat.fire(p, target, weapon, car)) {
          const rate = p.elite
            ? BALANCE.eliteFireRate
            : s.faction === 'hostile'
              ? BALANCE.hostileFireRate
              : 1;
          this.nextFire.set(
            s.id,
            this.time + (weapon === 'roof-gun' ? 0.4 : weapon === 'rifle' ? 1.2 : 4) / rate,
          );
          if (
            !car &&
            !this.landingEgress.has(s.id) &&
            target.category === 'person' &&
            target.state !== 'parachuting' &&
            p.p.distanceToSquared(target.p) < 40 * 40
          ) {
            this.population.civil?.cancelTask(p, 'Holding a supported firing position');
            p.controlled = true;
          }
        }
      }
      if (
        car ||
        this.landingEgress.has(s.id) ||
        (target.category === 'person' &&
          target.state !== 'parachuting' &&
          p.p.distanceToSquared(target.p) < 40 * 40)
      )
        return;
    }
    if (gunner) return;
    if (this.landingEgress.has(s.id)) return;
    if (s.faction === 'friendly' && s.index % 20 === 0 && (this.nextFire.get(s.id) ?? 0) <= this.time) {
      const flight = this.flights
        .filter((g) => g.aircraft.state !== 'wreck' && g.crew.some((u) => this.hostileById.has(u.id)))
        .sort((a, b) => a.aircraft.p.distanceToSquared(p.p) - b.aircraft.p.distanceToSquared(p.p))[0];
      if (
        flight &&
        this.combat.fireAtStructure(
          p,
          flight.aircraft.p.clone().add(v(0, 5, 0)),
          'rocket',
          undefined,
          flight.aircraft.cluster.building.id,
        )
      ) {
        this.nextFire.set(s.id, this.time + 5);
        return;
      }
    }
    if (s.faction === 'hostile' && s.index % 11 === 0 && (this.nextCharge.get(s.id) ?? 0) <= this.time) {
      this.nextCharge.set(s.id, this.time + 12);
      if (this.charges.tryPlant(s)) return;
    }
    const rally =
      s.faction === 'friendly'
        ? DEFENCE_POSTS[s.index % DEFENCE_POSTS.length]
        : {
            x: [-360, -80, 170, 420, -720][s.index % 5],
            y: 0,
            z: [250, 60, -180, 340, -80][Math.floor(s.index / 5) % 5],
          };
    this.route(
      s,
      rally,
      s.faction === 'friendly' ? 'defending residents' : 'advancing through the city',
      s.faction === 'hostile' && s.index % 11 === 0,
    );
  }
  /** Clear the supported pier before taking a stationary firing position. The
   * same originals use the resident walking/obstacle controller, including
   * after Cancel, so the last people crossing never trap the departing boat. */
  private clearGangways(): void {
    const civil = this.population.civil!;
    const deadline = performance.now() + 0.6;
    let requests = 0;
    for (const [id, e] of this.landingEgress) {
      const p = e.soldier.person;
      if (!alive(e.soldier) || p.state === 'falling') {
        this.landingEgress.delete(id);
        this.march.delete(id);
        continue;
      }
      if (
        p.p.z < e.landing.shore.z - 1 &&
        p.p.distanceTo(v(e.landing.door.x, e.landing.door.y, e.landing.door.z)) > 50
      ) {
        this.landingEgress.delete(id);
        this.march.delete(id);
        continue;
      }
      const task = civil.task(p);
      if (task && task.token === e.token && task.state === 'active') {
        const progress = this.march.get(id);
        if (!progress || progress.token !== task.token || progress.point.distanceToSquared(p.p) > 1) {
          this.march.set(id, { token: task.token, point: p.p.clone(), time: this.time });
          continue;
        }
        if (this.time - progress.time < 8 || civil.taskPlanning(p)) continue;
        civil.cancelTask(p, 'Blocked landing exit; reserving another shoreward path');
        this.march.delete(id);
        e.soldier.sequence += 13;
      }
      if (e.retry > this.time || requests >= 2 || performance.now() >= deadline) continue;
      e.retry = this.time + 0.8;
      requests++;
      const nodes = this.nodes(`pier-egress:${e.landing.id}`, e.landing.shore, 140).filter(
        (n) => n.z < e.landing.shore.z - 4 && Math.hypot(n.x - e.landing.door.x, n.z - e.landing.door.z) > 65,
      );
      for (let i = 0; i < Math.min(8, nodes.length); i++) {
        const n = nodes[(e.soldier.index * 7 + e.soldier.sequence + i) % nodes.length];
        if (this.population.hazardAt(n)) continue;
        const token = `pier-egress:${id}:${++this.taskSerial}`;
        if (civil.assignTask(p, civil.nodePlace(n), token, 'clearing the naval gangway', 1)) {
          e.token = token;
          break;
        }
      }
    }
  }
  private moveVehicle(): void {
    const army = this.population.military!,
      vehicles = [...army.patrols.map((p) => ({ car: p.car, crew: p.crew })), ...army.support.units];
    const group = vehicles[this.vehicleCursor++ % vehicles.length];
    if (!group) return;
    const car = group.car,
      driver = group.crew[0];
    if (
      car.state === 'wreck' ||
      !alive(driver) ||
      driver.state !== 'aboard' ||
      group.crew.some((s) => s.transition) ||
      group.crew.some((s) => s.seat !== 0 && !army.isGunner(s) && s.state === 'aboard')
    )
      return;
    if (car.plan.length && car.incident === `invasion:${this.serial}`) return;
    if ((this.decisions.get(car.id) ?? 0) > this.time) return;
    this.decisions.set(car.id, this.time + 12);
    const target = DEFENCE_POSTS[this.vehicleCursor % DEFENCE_POSTS.length];
    const approach = this.population.network
      .approaches(target, true, car.length)
      .find((a) => this.population.plannedRoute(car, a.point, a.node, true).length);
    if (!approach) return;
    car.plan = this.population.plannedRoute(car, approach.point, approach.node, true);
    car.incident = `invasion:${this.serial}`;
    car.speed = car.kind === 'tank' ? 5 : 9;
    car.state = 'enroute';
  }
  cancel(reason = 'Cancelled by player'): void {
    if (!this.active) return;
    this.result = reason;
    this.phase = 'ended';
    this.reason = 'Offensive actions stopped; survivors de-escalating';
    this.deployment = undefined;
    this.cancelledReserves = this.reserved;
    this.reserved = 0;
    this.combat.cancel();
    this.charges.cancel();
    this.population.airstrikes?.cancel('hostile');
    this.population.airstrikes?.cancel('friendly');
    this.population.military?.standDown();
    this.population.shelters?.cancel();
    if (this.population.drivers) this.population.drivers.alert = false;
    for (const g of this.boats) this.population.naval?.passengers.cancelLanding(g.ship);
    for (const g of this.flights) this.population.aviation?.troops.cancel(g.aircraft);
    this.population.military?.air.parachutes?.cancel();
    for (const s of this.hostile) {
      const healthy = alive(s);
      this.population.civil?.cancelTask(s.person, 'Cease fire; original survivor deactivated for withdrawal');
      s.person.demobilised = true;
      if (healthy && !s.person.carrier && s.state !== 'airborne' && s.person.state !== 'falling') {
        s.state = 'withdrawn';
        s.person.controlled = true;
        s.person.state = 'surrendering';
      }
    }
    for (const car of this.population.cars)
      if (car.incident?.startsWith('invasion:')) {
        car.incident = undefined;
        car.speed = car.kind === 'tank' ? 5 : 8;
      }
    this.record(reason);
    this.onMessage(`${reason}. Existing fires, casualties and rubble remain.`);
  }
  private cleanup(): void {
    const list = this.active ? this.retired : [...this.retired, ...this.hostile],
      civil = this.population.civil!;
    if (!list.length) return;
    for (let i = 0; i < 8; i++) {
      const s = list[this.cleanupCursor++ % list.length],
        p = s.person;
      if (
        p.fatal ||
        this.landingEgress.has(s.id) ||
        p.carrier ||
        ['falling', 'incapacitated', 'carried', 'recovering', 'parachuting'].includes(p.state) ||
        !this.population.people.includes(p)
      )
        continue;
      s.state = 'withdrawn';
      p.demobilised = true;
      const token = this.neutralTask.get(p.id),
        task = civil.task(p);
      if (token && task?.token === token && task.state === 'active') continue;
      if (token && task?.token === token && task.state === 'completed') {
        p.controlled = true;
        p.state = 'surrendering';
        continue;
      }
      if ((this.decisions.get(`neutral:${p.id}`) ?? 0) > this.time) continue;
      this.decisions.set(`neutral:${p.id}`, this.time + 8);
      const shore = this.city.strategic!.transport!.landings[1 + (s.index % 2)].shore,
        nodes = this.nodes(`neutral:${s.index % 2}`, shore, 80);
      for (let attempt = 0; attempt < 6; attempt++) {
        const n = nodes[(s.index * 5 + attempt) % nodes.length];
        if (!n) break;
        if (this.population.hazardAt(n)) continue;
        const id = `withdraw:${s.id}:${++this.taskSerial}`;
        if (civil.assignTask(p, civil.nodePlace(n), id, 'leaving combat', 2)) {
          this.neutralTask.set(p.id, id);
          break;
        }
      }
    }
  }
  update(dt: number): void {
    if (this.epoch !== this.physics.life.epoch) {
      this.reset();
      return;
    }
    this.time += dt;
    this.combat.update(dt);
    this.clearGangways();
    if (this.active) {
      if (this.time >= this.bombingAt && this.population.airstrikes) {
        this.bombingAt = this.time + 3;
        const plane = this.flights.find(
          (g) =>
            g.aircraft.state !== 'wreck' &&
            g.crew.length === 100 &&
            g.aircraft.p.y > 80 &&
            !this.population.airstrikes!.busy(g.aircraft),
        );
        const target = this.population.military?.friendly.find(
          (s) => alive(s) && !s.person.carrier && s.state === 'deployed',
        );
        if (plane && target) {
          const heading = target.person.p.clone().sub(plane.aircraft.p).setY(0).normalize();
          if (this.population.airstrikes.request(target.person.p, heading, 'hostile', plane.aircraft)) {
            this.bombingAt = this.time + 35;
            this.record('Original hostile aircrew commencing a coordinated bombing pass');
          }
        }
        const support = this.population.military?.air.aircraft;
        const attacker = this.hostile.find((s) => alive(s) && !s.person.carrier && s.state === 'deployed');
        if (
          support &&
          attacker &&
          this.population.airstrikes.request(
            attacker.person.p,
            attacker.person.p.clone().sub(support.p).setY(0).normalize(),
            'friendly',
            support,
          )
        )
          this.record('Original garrison aircrew commencing a support bombing pass');
      }
      this.capacity.update();
      if (this.deployment) {
        const start = performance.now(),
          end = start + 2;
        do {
          if (this.deployment.next().done) {
            this.deployment = undefined;
            break;
          }
        } while (performance.now() < end);
        this.setupMaxMs = Math.max(this.setupMaxMs, performance.now() - start);
      }
      this.charges.update(dt);
      const friendly = this.population.military!.friendly,
        total = friendly.length + this.hostile.length;
      for (let i = 0; i < Math.min(24, total); i++) {
        const index = this.cursor++ % total;
        // Interleave factions: processing all 600 defenders first gave them
        // up to 25 fixed ticks of earlier decisions at a contested arrival.
        // The same 24 decisions and complete rosters still receive fair turns.
        const s = combatTurn(friendly, this.hostile, index);
        if (s) this.brain(s);
      }
      this.counterAge -= dt;
      if (this.counterAge <= 0) {
        this.counterAge = 0.5;
        for (const s of this.hostile)
          if (s.person.fatal || s.person.state === 'incapacitated') s.state = 'incapacitated';
        const ending = invasionOutcome(
          friendly,
          this.hostile,
          this.reserved,
          !this.capacity.capturing && this.capacity.lost,
        );
        if (ending) this.cancel(ending);
        else if (this.phase === 'deploying' && this.hostile.some((s) => s.state === 'deployed')) {
          this.phase = 'combat';
          this.reason = 'Finite forces engaged; civilian refuge and ordinary services remain active';
        }
        if (this.active) this.moveVehicle();
      }
    }
    this.fleetAge -= dt;
    if (this.fleetAge <= 0) {
      this.fleetAge = 0.25;
      this.fleet();
    }
    if (!this.active || this.retired.length) this.cleanup();
  }
  reset(): void {
    this.combat.reset();
    this.charges.reset();
    this.capacity.clear();
    this.phase = 'idle';
    this.reason = this.result = '';
    this.hostile = [];
    this.hostileById.clear();
    this.retired = [];
    this.retiredById.clear();
    this.boats = [];
    this.flights = [];
    this.deployment = undefined;
    this.reserved = this.unavailable = this.cancelledReserves = 0;
    this.epoch = this.physics.life.epoch;
    this.time = this.serial = this.setupMaxMs = 0;
    this.goalNodes.clear();
    this.landingEgress.clear();
    this.march.clear();
    this.neutralTask.clear();
    this.decisions.clear();
    this.targetCursor.clear();
    this.nextFire.clear();
    this.nextCharge.clear();
    this.originVehicles.clear();
    this.log = [];
  }
  render(alpha: number): void {
    this.combat.render(alpha);
  }
  snapshot(): Record<string, number> {
    const friendly = this.population.military?.friendly ?? [],
      counts = rosterCounts(this.hostile);
    return {
      invasionActive: this.active ? 1 : 0,
      invasionEvent: this.serial,
      hostileCommitted: this.serial ? 600 : 0,
      hostileCreated: this.hostile.length,
      hostileReserved: this.reserved,
      hostileUnavailable: this.unavailable,
      hostileCancelledReserves: this.cancelledReserves,
      hostileCombatCapable: counts.combatCapable,
      hostileAboard: counts.aboard,
      hostileAirborne: counts.airborne,
      hostileDeployed: counts.deployed,
      hostileIncapacitated: counts.incapacitated,
      hostileDead: counts.dead,
      hostileLost: counts.lost,
      hostileWithdrawn: counts.withdrawn,
      hostileStateTotal:
        counts.dead +
        counts.lost +
        counts.incapacitated +
        counts.withdrawn +
        counts.aboard +
        counts.airborne +
        counts.deployed,
      friendlyCombatCapable: friendly.filter(alive).length,
      invasionSetupMaxMs: this.setupMaxMs,
      navalShoreward: this.landingEgress.size,
      ...this.capacity.snapshot(),
      ...this.combat.snapshot(),
      ...this.charges.snapshot(),
    };
  }
  inspect(): unknown {
    return {
      phase: this.phase,
      reason: this.reason,
      result: this.result,
      ...this.snapshot(),
      hostile: this.hostile.map((s) => ({
        id: s.id,
        state: s.state,
        role: s.role,
        carrier: s.person.carrier,
        fatal: !!s.person.fatal,
        demobilised: !!s.person.demobilised,
        p: s.person.p.toArray(),
      })),
      naval: this.boats.map((g) => ({
        id: g.ship.boat.id,
        state: g.ship.state,
        landing: g.landing.id,
        unloading: g.unloading,
        returning: g.returning,
      })),
      air: this.flights.map((g) => ({
        id: g.aircraft.id,
        state: g.aircraft.state,
        dropping: g.dropping,
        returning: g.returning,
      })),
      log: this.log,
      shoreward: [...this.landingEgress.values()].map((e) => ({
        id: e.soldier.id,
        landing: e.landing.id,
        task: e.token,
      })),
    };
  }
  dispose(): void {
    this.combat.dispose();
    this.charges.dispose();
  }
}
